import * as React from 'react';
import {CarouselMobile} from './CarouselMobile/CarouselMobile';
import {CarouselDesktop} from './CarouselDesktop/CarouselDesktop';
import s from './Carousel.scss';
import {Mousewheel} from 'swiper/modules';
import {Swiper, SwiperRef, SwiperSlide} from 'swiper/react';
import {useCallback, useRef, useState} from 'react';
import type {Swiper as SwiperClass} from 'swiper/types';
import {useStyles} from '@wix/tpa-settings/react';
import {useEnvironment} from '@wix/yoshi-flow-editor';
import {useStylesParams} from '../../../stylesParamsContext';
import {PaginationDots} from '../../../sliderGallery/components/SliderGallery/PaginationDots/PaginationDots';
import {classes as carouselDotsStyleable} from './CarouselDots.st.css';
import {ImageCarouselPaginationStyle, ImageCarouselPaginationVisibility} from '../../../types/galleryTypes';
import {PaginationLines} from './PaginationLines/PaginationLines';

export interface CarouselAriaLabels {
  arrowLeft: string;
  arrowRight: string;
}

export interface CarouselProps {
  shouldShowArrowsOnHover: boolean;
  ariaLabels: CarouselAriaLabels;
  imageCount: number;
  imageAltTexts?: string[];
  children?: React.ReactNode;
}

export const Carousel: React.FC<CarouselProps> = (props) => {
  const swiperRef = useRef<SwiperRef>(null);
  const [swiper, setSwiper] = useState<SwiperClass>(null);
  const [activeSlide, setActiveSlide] = useState<number>(0);

  const {isRTL, isMobile} = useEnvironment();
  const slideNext = useCallback(/* istanbul ignore next */ () => swiper?.slideNext(), [swiper]);
  const slidePrev = useCallback(/* istanbul ignore next */ () => swiper?.slidePrev(), [swiper]);
  const styles = useStyles();
  const stylesParams = useStylesParams();

  const showPagination = styles.get(stylesParams.gallery_imageCarouselShowPagination);
  const paginationVisibilityValue = styles.get(stylesParams.gallery_imageCarouselPaginationVisibility);
  const paginationStyleValue = styles.get(stylesParams.gallery_imageCarouselPaginationStyle);
  const showNavigationArrows = styles.get(stylesParams.gallery_imageCarouselShowNavigationArrows);

  const paginationVisibility = paginationVisibilityValue?.value as ImageCarouselPaginationVisibility;
  const paginationStyle = paginationStyleValue?.value as ImageCarouselPaginationStyle;

  const {shouldShowArrowsOnHover, ariaLabels, imageCount, imageAltTexts, children} = props;

  // In RTL, Swiper's slideNext/slidePrev are visually reversed, so we swap the arrow actions
  const leftArrowAction = isRTL ? slideNext : slidePrev;
  const rightArrowAction = isRTL ? slidePrev : slideNext;

  const showDots = showPagination && paginationStyle === ImageCarouselPaginationStyle.DOTS;
  const showLines = showPagination && paginationStyle === ImageCarouselPaginationStyle.LINES;

  const renderDots = () => (
    <PaginationDots
      swiperCurrentIndex={/* istanbul ignore next */ activeSlide ?? 0}
      totalProducts={imageCount}
      slidesCount={1}
      shouldLoop={false}
      productsAmountPerSwipe={1}
      dotNavigationClassName={carouselDotsStyleable.root}
    />
  );

  const renderLines = () => <PaginationLines activeSlide={activeSlide} totalSlides={imageCount} isRTL={isRTL} />;

  const getSwiper = () => {
    return (
      <Swiper
        className={s.swiperRoot}
        wrapperClass={s.swiperWrapper}
        dir={/* istanbul ignore next */ isRTL ? 'rtl' : 'ltr'}
        modules={[Mousewheel]}
        threshold={2}
        loop={true}
        speed={600}
        simulateTouch={false}
        slidesPerView={1}
        watchSlidesProgress={true}
        onSwiper={setSwiper}
        ref={swiperRef}
        onActiveIndexChange={/* istanbul ignore next */ (swiperInstance) => setActiveSlide(swiperInstance?.realIndex)}>
        {React.Children.toArray(children).map((image, i) => (
          <SwiperSlide key={i} className={s.swiperSlide} tabIndex={-1}>
            {image}
          </SwiperSlide>
        ))}
      </Swiper>
    );
  };

  const paginationElement = (showDots && renderDots()) || (showLines && renderLines());

  const ariaLiveContent = imageAltTexts?.[activeSlide] ?? '';

  return (
    <>
      <div className={s.srOnly} aria-live="polite" aria-atomic="true">
        {ariaLiveContent}
      </div>
      {isMobile ? (
        <CarouselMobile
          activeSlide={activeSlide}
          slidesCount={imageCount}
          slideTo={swiper?.slideTo}
          pagination={paginationElement}
          showNavigationArrows={showNavigationArrows}
          ariaLabels={ariaLabels}
          slideNext={rightArrowAction}
          slidePrev={leftArrowAction}>
          {getSwiper()}
        </CarouselMobile>
      ) : (
        <CarouselDesktop
          ariaLabels={ariaLabels}
          shouldShowArrowsOnHover={shouldShowArrowsOnHover}
          showNavigationArrows={showNavigationArrows}
          paginationVisibility={paginationVisibility}
          slideNext={rightArrowAction}
          slidePrev={leftArrowAction}
          pagination={paginationElement}>
          {getSwiper()}
        </CarouselDesktop>
      )}
    </>
  );
};
